import config from '../config';
import {listConstants} from '../components/contexts/List';


/**
 * Returns only the hostname of the given url
 *
 * @param url {string} URL to extract hostname from
 * @return {string} Extracted hostname
 */
export const extractHostname = url => {

    return url.replace(/^https?:(\d*)\/\//, '')
        .replace(/^\/\//, '')
        .replace(/\/.*$/, '');
};


/**
 * Parses the given string value into it's specific type
 *
 * @param value
 * @return {boolean|number}
 */
const parseUrlValue = value => {

    if (value === 'true') {

        return true;
    }

    if (value === 'false') {

        return false;
    }

    if (value.match(/^\d+$/)) {

        return parseInt(value, 10);
    }

    if (value.match(/^\d+\.\d+$/)) {

        return parseFloat(value);
    }

    return value;
};


/**
 * Converts the value into its url vale
 *
 * @param value {any}
 * @return {string}
 */
const toUrlValue = value => {

    if (value === undefined || null) {

        return '';
    }

    return String(value);
};


/**
 * Constructs a query string for the given arguments
 *
 * @param params {object} Key value parameters
 * @return {string} Query string
 */
export const buildQueryString = params => {

    return Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(toUrlValue(value))}`)
        .join('&');
};


/**
 * Parses a query string and returns a key value object
 *
 * @param queryString {string} Query string
 * @return {object} Key value parameters
 */
export const parseQueryString = (queryString = '') => {

    if (!queryString) {

        return {};
    }

    const queryParams = {};
    const urlSearchParams = new URLSearchParams(queryString);
    urlSearchParams.forEach((value, key) => {
        queryParams[key] = value ? parseUrlValue(value) : null;
    });

    return queryParams;
};


/**
 * Extracts current query string
 *
 * @return {object}
 */
export const getQueryParams = () => {

    return parseQueryString(window.location.search);
};


/**
 * @return {string} Full path
 */
export const getPath = () => {

    return window.location.pathname;
};


/**
 * Substitutes the given path-place-holders (either by :varName or {varName}) with the provided variables
 *
 * @param path {String} Path to substitute
 * @param pathVariables {Object} Key value pair
 * @return Substituted path
 */
export const buildPath = (path, pathVariables = {}) => {

    Object.keys(pathVariables)
        .forEach(pathVariableName => {

            const pathVariableValue = pathVariables[pathVariableName];

            // supporting both styles of path variable declaration
            path = path.replace(`:${pathVariableName}`, pathVariableValue);
            path = path.replace(`{${pathVariableName}}`, pathVariableValue);
        });

    return path;
};


/**
 * @param path {string} Path with path variables placeholders
 * @param pathVariables {object} Path variables to use in path
 * @param queryStringParams {object} Query string params
 * @return {string} Absolute path and query string URL
 */
export const buildUrl = (path, pathVariables = {}, queryStringParams = {}) => {

    const finalPath = buildPath(path, pathVariables);
    const queryString = buildQueryString(queryStringParams);

    return `${finalPath}${queryString ? '?' + queryString : ''}`;
};


/**
 * @param path {string} Path with path variables placeholders
 * @param pathVariables {object} Path variables to use in path
 * @param queryStringParams {object} Query string params
 * @return {string} URL including hostname, path and query string params
 */
export const buildFullUrl = (path, pathVariables = {}, queryStringParams = {}) => {

    const url = buildUrl(path, pathVariables, queryStringParams);

    return `${config.host.uri}${url}`;
};


/**
 * @returns {string} Current full path and optional query strings
 */
export const getUrl = () => {

    return buildFullUrl(getPath(), {}, getQueryParams());
};


/**
 * @param listFilter {object}
 * @return {string}
 */
export const listRequestFilterToQueryString = ({property, type, value}) => {

    return `${property},,${type}~~${value}`;
};


/**
 * @param listFilterQueryString {string}
 * @return {object} listFilter
 */
export const listFilterQueryStringToRequestFilter = listFilterQueryString => {

    const parts = listFilterQueryString.split(',,');
    if (parts.length !== 2) {

        throw Error(`Wrong list filter query string argument: ${listFilterQueryString}`);
    }

    const property = parts[0];
    const remainingParts = parts[1].split('~~');
    const type = remainingParts.length === 2 ? remainingParts[0] : listConstants.LIST_FILTER_TYPE_EQUALS;
    const value = remainingParts.pop();

    return {
        property,
        type,
        value
    };
};


/**
 * @param listSortByQueryString {string}
 * @return {object} listFilter
 */
export const listSortByQueryStringToRequestSortBy = listSortByQueryString => {

    const parts = listSortByQueryString.split(',,');
    const property = parts[0];
    const dir = parts.length === 2 ? parts[1] : listConstants.SORT_DIR_ASC;

    return {
        dir,
        property
    };
};


/**
 * @param listRequest {object} List request params
 * @return {object} GET request value representation of filtering
 */
export const listRequestToQueryParams = listRequest => {

    const filterQueryString = (listRequest.filter || [])
        .map(listFilter => listRequestFilterToQueryString(listFilter))
        .join(';;');
    const sortByQueryString = (listRequest.sortBy || [])
        .map(({dir, property}) => `${property},,${dir}`)
        .join(';;');

    return {
        f: filterQueryString,
        p: listRequest.page,
        ps: listRequest.pageSize,
        s: sortByQueryString
    };
};


/**
 * @param queryString {string} Query string
 * @return {object} GET request value representation of filtering
 */
export const queryStringToListRequest = queryString => {

    const externalForm = parseQueryString(queryString);

    const filter = (externalForm.f || '')
        .split(';;')
        .filter(parts => parts.trim() !== '')
        .map(listFilterQueryString => listFilterQueryStringToRequestFilter(listFilterQueryString));

    const sortBy = (externalForm.s || '')
        .split(';;')
        .filter(parts => parts.trim() !== '')
        .map(listSortByQueryString => listSortByQueryStringToRequestSortBy(listSortByQueryString));

    return {
        filter,
        page: externalForm.p,
        pageSize: externalForm.ps,
        sortBy
    };
};
